import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';

import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';

import ActivityPlanningFilterBar from './ActivityPlanningFilterBar';
import ActivityTreePanel from './ActivityTreePanel';
import ActivityTaskBudgetPanel from './ActivityTaskBudgetPanel';
import ActivityFormPanel from './ActivityFormPanel';
import BudgetImpactPanel from './BudgetImpactPanel';
import TemplateLibraryPanel from './TemplateLibraryPanel';
import KpiPlanGridPanel from './KpiPlanGridPanel';
import ActivityPlanningCostCenterSelector from './ActivityPlanningCostCenterSelector';

import {
  ActivityCandidate,
  ActivityNode,
  ActivityPlanningCards,
  ActivityPlanningWorkbenchModel,
  ActivityTemplate,
  BudgetLineRow,
  TaskRow,
  IndicatorCandidate,
  KpiPlanRow,
} from './activityplanning.models';

//import { ActivityPlanningPageProps } from './planningContext.types';

import {
  fetchGlobalObjectiveCombo,
  fetchSpecificObjectiveCombo,
  fetchIndicatorCandidates,
  addIndicator,
  saveIndicators,
  addActivity,
  applyTemplate,
  fetchActivityCandidates,
  fetchActivityPlanningWorkbench,
  fetchBudgetLines,
  fetchTemplates,
  saveBudgetLines,
  setPlanningCostCenter,
  validateActivityPlanning,
} from './activityplanning.apis';

import { fetchCombo } from '../core/coreapi';
import {
  normalizeKpiPlanRows,
  normalizeWorkbenchTree,
  summaryCards,
} from './activityplanning.normalizers';
import { ComboRow } from '../core/coremodels';

//import { PlanningWorkbenchShell } from '../planningcore';

type State = {
  model?: ActivityPlanningWorkbenchModel;
  planningCycleId: number | null;
  budgetPeriodName: string;
  cycleCode: string;
  axisCode: string;
  globalObjectiveCode: string;
  specificObjectiveCode: string;
  cycles: ComboRow[];
  axes: ComboRow[];
  globalObjectives: ComboRow[];
  specificObjectives: ComboRow[];
  accounts: ComboRow[];
  activities: ActivityNode[];
  selectedNode: ActivityNode | null;
  taskRows: TaskRow[];
  budgetRows: BudgetLineRow[];
  cards: ActivityPlanningCards | null;
  templates: ActivityTemplate[];
  indicatorCandidates: IndicatorCandidate[];
  kpiPlanRows: KpiPlanRow[];
  activityCandidates: ActivityCandidate[];
  selectedActivityCandidate: ActivityCandidate | null;
  driverValue: number;
  targetValue: number;
  activityWeight: number;
  activityDescription: string;
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;
};

export default class ActivityPlanningWorkbench extends React.Component<
  PlanningPageProps,
  State
> {
  state: State = {
    model: undefined,
    planningCycleId: this.props.plancontext?.pci ?? this.props.planningContext?.pci ?? null,
    budgetPeriodName: this.props.plancontext?.bpn ?? this.props.planningContext?.bpn ?? '',
    cycleCode: '',
    axisCode: '',
    globalObjectiveCode: '',
    specificObjectiveCode: '',
    cycles: [],
    axes: [],
    globalObjectives: [],
    specificObjectives: [],
    accounts: [],
    activities: [],
    selectedNode: null,
    taskRows: [],
    budgetRows: [],
    cards: null,
    templates: [],
    indicatorCandidates: [],
    kpiPlanRows: [],
    activityCandidates: [],
    selectedActivityCandidate: null,
    driverValue: 0,
    targetValue: 0,
    activityWeight: 1,
    activityDescription: '',
    loading: false,
    saving: false,
    error: '',
    success: '',
  };

  componentDidMount() {
    void this.initialize();
  }

  componentDidUpdate(previous: PlanningPageProps) {
    const before =
      previous.plancontext?.pci ?? previous.planningContext?.pci ?? null;
    const current =
      this.props.plancontext?.pci ?? this.props.planningContext?.pci ?? null;
    if (Number(before || 0) !== Number(current || 0)) {
      this.setState(
        {
          planningCycleId: current,
          budgetPeriodName:
            this.props.plancontext?.bpn ??
            this.props.planningContext?.bpn ??
            '',
        },
        () => void this.initialize()
      );
    }
  }

  private pci = () =>
    Number(
      this.state.planningCycleId ??
        this.props.plancontext?.pci ??
        this.props.planningContext?.pci ??
        0
    );

  private initialize = async () => {
    if (!this.pci())
      return this.setState({ error: 'Planning Cycle context is missing.' });
    this.setState({ loading: true, error: '', success: '' });
    try {
      const [model, indicatorCandidates, templates, cycles, axes, accounts] = await Promise.all([
        fetchActivityPlanningWorkbench(this.pci()),
        fetchIndicatorCandidates(this.pci()).catch(() => []),
        fetchTemplates().catch(() => []),
        fetchCombo('budgetcycle').catch(() => []),
        fetchCombo('axis').catch(() => []),
        fetchCombo('chartofaccounts').catch(() => []),
      ]);
      this.setState({
        model,
        indicatorCandidates,
        kpiPlanRows: normalizeKpiPlanRows(model),
        activities: normalizeWorkbenchTree(model),
        templates,
        cycles,
        axes,
        accounts,
        selectedNode: null,
        taskRows: [],
        budgetRows: [],
        cards: summaryCards(model, null),
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private reload = async (selected?: ActivityNode | null) => {
    const [model, indicatorCandidates] = await Promise.all([
      fetchActivityPlanningWorkbench(this.pci()),
      fetchIndicatorCandidates(this.pci()).catch(() => []),
    ]);
    const activities = normalizeWorkbenchTree(model);
    const selectedNode = selected
      ? (activities.find((x) => x.nid === selected.nid) ?? null)
      : null;
    this.setState({
      model,
      indicatorCandidates,
      kpiPlanRows: normalizeKpiPlanRows(model),
      activities,
      selectedNode,
      cards: summaryCards(model, selectedNode),
    });
    if (selectedNode?.nodetype === 'ACTIVITY')
      await this.loadActivity(selectedNode, model);
  };

  private loadActivity = async (
    activity: ActivityNode,
    model = this.state.model
  ) => {
    if (!model) return;
    const taskRows: TaskRow[] = normalizeWorkbenchTree(model)
      .filter(
        (x) =>
          x.nodetype === 'TASK' &&
          Number(x.activityplanid) === Number(activity.activityplanid)
      )
      .map((x) => ({
        id: x.taskplanid ?? x.rid,
        rid: x.taskplanid ?? x.rid,
        taskplanid: x.taskplanid ?? x.rid,
        taskid: x.taskid ?? null,
        activityplanid: x.activityplanid ?? null,
        code: x.code,
        name: x.name,
        description: x.description ?? '',
        weight: Number(x.raw?.weight ?? x.raw?.taskweight ?? 1),
        status: x.status ?? '',
        plannedamount: Number(x.totalamount ?? 0),
        budgetlinecount: Number(x.budgetlinecount ?? 0),
        budgetstate: Number(x.budgetstate ?? 0),
        _state: 'clean',
        raw: x.raw,
      }));
    const collections = await Promise.all(
      taskRows.map((x) =>
        fetchBudgetLines(Number(x.taskplanid)).catch(() => [])
      )
    );
    this.setState({
      taskRows,
      budgetRows: collections.flat(),
      cards: summaryCards(model, activity),
    });
  };

  private selectNode = async (node: ActivityNode) => {
    this.setState({
      selectedNode: node,
      activityCandidates: [],
      selectedActivityCandidate: null,
      taskRows: [],
      budgetRows: [],
      cards: summaryCards(this.state.model, node),
      error: '',
      success: '',
    });
    try {
      if (node.nodetype === 'KPI') {
        const activityCandidates = await fetchActivityCandidates(
          Number(node.kpiplanid || node?.rid || 0),
          this.pci(),
          this.state.model?.context.ccc || ''
        ).catch(() => []);
        this.setState({ activityCandidates });
      } else if (node.nodetype === 'ACTIVITY') {
        await this.loadActivity(node);
      } else if (node.nodetype === 'TASK') {
        const budgetRows = await fetchBudgetLines(
          Number(node.taskplanid || node.rid)
        );
        const taskRows: TaskRow[] = [
          {
            id: node.taskplanid ?? node.rid,
            rid: node.taskplanid ?? node.rid,
            taskplanid: node.taskplanid ?? node.rid,
            taskid: node.taskid ?? null,
            activityplanid: node.activityplanid ?? null,
            code: node.code,
            name: node.name,
            description: node.description ?? '',
            weight: Number(node.raw?.weight ?? 1),
            status: node.status ?? '',
            plannedamount: Number(node.totalamount ?? 0),
            budgetlinecount: Number(node.budgetlinecount ?? 0),
            budgetstate: Number(node.budgetstate ?? 0),
            _state: 'clean',
            raw: node.raw,
          },
        ];
        this.setState({ taskRows, budgetRows });
      }
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private changeCostCenter = async (costcenterid: number) => {
    const stamp = this.state.model?.context.profilestamp;
    if (!stamp)
      return this.setState({
        error: 'Planning Context stamp is unavailable. Refresh and retry.',
      });
    this.setState({ saving: true, error: '', success: '' });
    try {
      await setPlanningCostCenter(this.pci(), costcenterid, stamp);
      await this.initialize();
      this.setState({ success: 'Working Cost Center changed.' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private addActivity = async () => {
    const node = this.state.selectedNode;
    const candidate = this.state.selectedActivityCandidate;
    if (node?.nodetype !== 'KPI')
      return this.setState({
        error: 'Select an Indicator before adding an Activity.',
      });
    if (!candidate) return this.setState({ error: 'Select an Activity.' });
    this.setState({ saving: true, error: '', success: '' });
    try {
      const result: any = await addActivity({
        kpiplanid: Number(node.kpiplanid || node.rid),
        activityid: candidate.rid,
        planningcycleid: this.pci(),
        weight: this.state.activityWeight,
        drivervalue: this.state.driverValue,
        targetvalue: this.state.targetValue,
        description: this.state.activityDescription,
      });
      this.setState({
        selectedActivityCandidate: null,
        driverValue: 0,
        targetValue: 0,
        activityWeight: 1,
        activityDescription: '',
        success: `Activity added successfully. ${Number(result?.taskcount || 0)} reusable Task(s) were added automatically.`,
      });
      await this.reload();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private saveBudgetRows = async (rows: any[]) => {
    const dirty = (rows || []).filter((x) => x._state && x._state !== 'clean');
    if (!dirty.length) return;
    const taskplanid = Number(
      dirty[0]?.taskplanid || dirty[0]?.raw?.taskplanid || 0
    );
    if (!taskplanid)
      throw new Error('Select a Task before saving budget lines.');
    this.setState({ saving: true, error: '', success: '' });
    try {
      await saveBudgetLines(taskplanid, dirty);
      this.setState({ success: 'Task budget saved.' });
      await this.reload(this.state.selectedNode);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
      throw error;
    } finally {
      this.setState({ saving: false });
    }
  };


  private addIndicator = async (payload: {
    candidate: IndicatorCandidate;
    baselinevalue: number;
    targetvalue: number;
    thresholdmin?: number | null;
    thresholdmax?: number | null;
    description: string;
  }) => {
    this.setState({ saving: true, error: '', success: '' });

    try {
      await addIndicator({
        kpiid: payload.candidate.rid,
        planningcycleid: this.pci(),
        baselinevalue: payload.baselinevalue,
        targetvalue: payload.targetvalue,
        thresholdmin: payload.thresholdmin,
        thresholdmax: payload.thresholdmax,
        description: payload.description,
      });

      this.setState({
        success: 'Indicator added successfully.',
      });

      await this.reload();
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
      throw error;
    } finally {
      this.setState({ saving: false });
    }
  };

  private saveIndicators = async (rows: KpiPlanRow[]) => {
    this.setState({ saving: true, error: '', success: '' });

    try {
      await saveIndicators(rows);

      this.setState({
        success: 'Indicators saved.',
      });

      await this.reload(this.state.selectedNode);
    } catch (error: any) {
      this.setState({
        error: error?.message || String(error),
      });
      throw error;
    } finally {
      this.setState({ saving: false });
    }
  };

  private validate = async () => {
    this.setState({ saving: true, error: '', success: '' });
    try {
      await validateActivityPlanning(this.pci());
      this.setState({ success: 'Activity Planning structure is valid.' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private applyTemplate = async (templateCode: string) => {
    const node = this.state.selectedNode;
    if (node?.nodetype !== 'ACTIVITY')
      return this.setState({
        error: 'Select an Activity before applying a template.',
      });
    this.setState({ saving: true, error: '', success: '' });
    try {
      await applyTemplate(
        Number(node.activityplanid || node.rid),
        templateCode
      );
      this.setState({ success: 'Template applied successfully.' });
      await this.reload(node);
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  private onAxisChange = async (axisCode: string) => {
    this.setState({
      axisCode,
      globalObjectiveCode: '',
      specificObjectiveCode: '',
      globalObjectives: [],
      specificObjectives: [],
    });
    if (!axisCode) return;
    try {
      this.setState({
        globalObjectives: await fetchGlobalObjectiveCombo(axisCode),
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private onGlobalObjectiveChange = async (globalObjectiveCode: string) => {
    this.setState({
      globalObjectiveCode,
      specificObjectiveCode: '',
      specificObjectives: [],
    });
    if (!this.state.axisCode || !globalObjectiveCode) return;
    try {
      this.setState({
        specificObjectives: await fetchSpecificObjectiveCombo(
          this.state.axisCode,
          globalObjectiveCode
        ),
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  render() {
    const model = this.state.model;
    const selected = this.state.selectedNode;
    const costCenters = (model?.context.costcenters || []).map((x) => ({
      rid: x.id,
      shc: x.code,
      nam: x.name,
    }));
    const filters = (
      <ActivityPlanningFilterBar
        cycleCode={this.state.cycleCode}
        planningContext={
          this.props.plancontext ?? this.props.planningContext ?? null
        }
        onChangePlanningCycle={
          this.props.openPlanningCycleSwitcher ?? (() => undefined)
        }
        costCenterCode={model?.context.ccc ?? ''}
        axisCode={this.state.axisCode}
        globalObjectiveCode={this.state.globalObjectiveCode}
        specificObjectiveCode={this.state.specificObjectiveCode}
        cycles={this.state.cycles}
        costCenters={costCenters}
        axes={this.state.axes}
        globalObjectives={this.state.globalObjectives}
        specificObjectives={this.state.specificObjectives}
        onCycleChange={(cycleCode) => this.setState({ cycleCode })}
        onCostCenterChange={(code) => {
          const x = model?.context.costcenters.find((c) => c.code === code);
          if (x) void this.changeCostCenter(x.id);
        }}
        onAxisChange={this.onAxisChange}
        onGlobalObjectiveChange={this.onGlobalObjectiveChange}
        onSpecificObjectiveChange={(specificObjectiveCode) =>
          this.setState({ specificObjectiveCode })
        }
        onRefresh={this.initialize}
        onClear={() =>
          this.setState({
            axisCode: '',
            globalObjectiveCode: '',
            specificObjectiveCode: '',
            globalObjectives: [],
            specificObjectives: [],
          })
        }
        loading={this.state.loading || this.state.saving}
      />
    );

    return (
      <PlanningWorkbenchShell
        {...this.props}
        title={this.props.title || 'Activity Planning'}
        subtitle="Prepare Indicators, Activities, Tasks and task-level budgets for the selected Cost Center."
        icon={<LocalActivityOutlinedIcon />}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        filters={filters}
        actions={
          <Button variant="outlined" onClick={this.validate}>
            Validate
          </Button>
        }
        onRefresh={this.initialize}
        headerExtension={
          model?.context.multiplecostcenters ? (
            <ActivityPlanningCostCenterSelector
              value={model.context.cci}
              options={model.context.costcenters}
              disabled={this.state.loading || this.state.saving}
              onChange={this.changeCostCenter}
            />
          ) : (
            <Chip
              size="small"
              label={`${model?.context.ccc ?? ''}${model?.context.ccn ? ` — ${model.context.ccn}` : ''}`}
            />
          )
        }
      >
        <Box sx={{ p: 3 }}>
          {(this.state.loading || this.state.saving) && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <CircularProgress size={22} />
              <Typography>
                {this.state.saving
                  ? 'Saving...'
                  : 'Loading Activity Planning...'}
              </Typography>
            </Stack>
          )}
          <BudgetImpactPanel cards={this.state.cards} />

          <Box sx={{ mb: 3 }}>
            <KpiPlanGridPanel
              rows={this.state.kpiPlanRows}
              candidates={this.state.indicatorCandidates}
              loading={this.state.loading || this.state.saving}
              editable={!!model?.actions.canAddIndicator}
              onRowsChange={(kpiPlanRows) =>
                this.setState({ kpiPlanRows })
              }
              onSave={this.saveIndicators}
              onAdd={this.addIndicator}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                <ActivityTreePanel
                  activities={this.state.activities}
                  selectedNid={selected?.nid}
                  onSelect={(node) => void this.selectNode(node)}
                />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <ActivityFormPanel
                selectedNode={selected}
                loading={this.state.loading}
                saving={this.state.saving}
                planningCycleName={
                  this.props.plancontext?.pcn ??
                  this.props.planningContext?.pcn ??
                  ''
                }
                budgetPeriodName={this.state.budgetPeriodName}
                costCenterCode={model?.context.ccc ?? ''}
                activityCandidates={this.state.activityCandidates}
                selectedActivity={this.state.selectedActivityCandidate}
                activityWeight={this.state.activityWeight}
                driverValue={this.state.driverValue}
                targetValue={this.state.targetValue}
                activityDescription={this.state.activityDescription}
                onActivitySelect={(selectedActivityCandidate) =>
                  this.setState({ selectedActivityCandidate })
                }
                onFieldChange={(name, value) =>
                  this.setState({ [name]: value } as any)
                }
                addActivity={this.addActivity}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 5 }}>
              {selected?.nodetype === 'ACTIVITY' ? (
                <Stack spacing={2}>
                  <ActivityTaskBudgetPanel
                    taskRows={this.state.taskRows}
                    budgetRows={this.state.budgetRows}
                    accounts={this.state.accounts}
                    loading={this.state.loading}
                    onTaskRowsChange={(taskRows) => this.setState({ taskRows })}
                    onBudgetRowsChange={(budgetRows) =>
                      this.setState({ budgetRows })
                    }
                    onSaveTasks={async () => undefined}
                    onSaveBudgetLines={this.saveBudgetRows}
                  />
                  <TemplateLibraryPanel
                    templates={this.state.templates}
                    applying={this.state.saving}
                    onApply={this.applyTemplate}
                  />
                </Stack>
              ) : (
                <Alert severity="info">
                  Select an Activity to review its Tasks and prepare task-level
                  budget lines.
                </Alert>
              )}
            </Grid>
          </Grid>
        </Box>
      </PlanningWorkbenchShell>
    );
  }
}
